import { createMapShell } from '../map/mapShell.js';
import type { ClientStore } from '../state/clientStore.js';
import type { ServerClock } from '../state/clock.js';
import { RENDERERS } from '../layers/registry.js';
import {
  buildSubwayLinesLayer,
  buildSubwayStationsLayer,
  subwayStationDirectionAxes,
} from '../layers/subwayMap.js';
import {
  lirrGeometryUrl,
  buildLirrLinesLayer,
  buildLirrStationsLayer,
} from '../layers/lirrMap.js';
import {
  mnrGeometryUrl,
  buildMnrLinesLayer,
  buildMnrStationsLayer,
} from '../layers/mnrMap.js';
import {
  pathGeometryUrl,
  buildPathLinesLayer,
  buildPathStationsLayer,
} from '../layers/pathMap.js';
import {
  buildStationExtentLayers,
  fetchStationExtents,
  orderSubwayStaticLayers,
  StationExtentSelection,
} from '../layers/stationExtents.js';
import { pickedStationId } from '../layers/stationSelection.js';
import { createEpochedGeometry } from '../layers/epochedGeometry.js';
import { subwayGeometryUrl } from '../layers/subwayGeometryUrl.js';
import type { GeometryEpochSource } from '../state/geometryEpochHub.js';
import { applyRadar } from '../layers/radar.js';
import { applyTraffic, fetchTomTomAttribution } from '../layers/traffic.js';
import { createTrafficController } from '../layers/trafficController.js';
import { createVitalsPanel } from '../vitals/panel.js';
import { startDayNight } from '../ambient/dayNight.js';
import type { Visibility } from '../hud/visibility.js';
import { createControlRail } from '../hud/controls/controlRail.js';
import { createLayersPanel } from '../hud/controls/layersPanel.js';
import { createAboutPanel } from '../hud/controls/aboutPanel.js';
import { FeedHealth } from '../hud/feedHealth.js';
import { createZoomControl } from '../hud/controls/zoomControl.js';
import { createThemeSwitcher } from '../hud/controls/themeSwitcher.js';
import { loadThemeId, themeAllowsTint, applyTheme } from '../map/mapTheme.js';
import { createCameraPopup } from '../map/cameraPopup.js';
import { createStationHoverCard } from '../map/stationHover.js';
import { featuresToBounds } from '../map/featureBounds.js';
import { Focus, STATIC_FOCUS_BOUNDS } from '../vitals/focus.js';
import type { CameraProps, SubwayGeometry, MapGeometry, SubwayStationExtentsPayload, SubwayStationArrival, SubwayTravelDirection } from '@vitals/shared';
import type { PickingInfo } from '@deck.gl/core';
import { GeoTracker } from '../map/geolocation.js';
import { buildUserLocationLayers } from '../layers/userLocationLayer.js';
import { createLocateButton } from '../hud/locateButton.js';
import { isInNyc } from '../map/nycBounds.js';
import { setFlightZoom } from '../layers/flightLabels.js';
import { isLayerSemanticallyVisible } from '../layers/semanticVisibility.js';
import type maplibregl from 'maplibre-gl';
import { ViewLifecycle } from './viewLifecycle.js';

interface RestorableCamera { center:[number,number];zoom:number;bearing:number;pitch:number }
export interface MapViewState extends RestorableCamera { focus:string|null;returnCamera?:RestorableCamera }
let geolocationAutoCenterComplete = false;
export function createMapView(options:{store:ClientStore;clock:ServerClock;visibility:Visibility;navigateTycoon:()=>void;initialState?:MapViewState;geometryEpochs?:GeometryEpochSource}):{frame():void;getState():MapViewState;destroy():void}{

document.getElementById('map')!.style.display='block';
const { map, overlay } = createMapShell('map');
const lifecycle = new ViewLifecycle();
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const cameraDuration = (): number => reduceMotion.matches ? 0 : 700;
setFlightZoom(map.getZoom());
const onFlightZoom = () => setFlightZoom(map.getZoom());
map.on('zoom', onFlightZoom);
lifecycle.own(() => map.off('zoom', onFlightZoom));
const {store,clock,visibility}=options;
if(options.initialState){map.jumpTo({center:options.initialState.center,zoom:options.initialState.zoom,bearing:options.initialState.bearing,pitch:options.initialState.pitch});}

const TOMTOM_API_KEY = import.meta.env.VITE_TOMTOM_API_KEY || undefined;
const isVisibilityAvailable = (id: string): boolean => id !== 'traffic' || TOMTOM_API_KEY !== undefined;
const focus = new Focus();
focus.set(options.initialState?.focus??null);

// Traffic cameras are the one map object with a persistent click action: show
// the live still. Everything else stays a lightweight hover tooltip.
const camPopup = createCameraPopup(document.body);
const stationExtentSelection = new StationExtentSelection();
const stationHover = createStationHoverCard({
  root: document.body,
  nowMs: () => clock.nowMs(),
  arrivalsFor: (selectionId) => {
    const summary = store.get('subway')?.summary as { stationArrivals?: Record<string, SubwayStationArrival[]> } | undefined;
    return summary?.stationArrivals?.[selectionId] ?? [];
  },
  axesFor: (selectionId) => subwayStationAxes[selectionId] ?? [],
});
overlay.setProps({
  onHover: (info: PickingInfo) => stationHover.handlePick(info),
  onClick: (info: PickingInfo) => {
    const object = info.object as { props?: CameraProps } | null;
    if (object?.props && info.layer?.id === 'cams') camPopup.show(object.props, info.x, info.y);
    else {
      camPopup.hide();
      const stationId = pickedStationId(info.layer?.id, info.object);
      if (typeof stationId === 'string') stationExtentSelection.select(stationId);
      else if (info.object === null) stationExtentSelection.clear();
    }
  },
});

// Vitals board (left panel). It exposes a controls footer slot so the map
// controls mount inside the same panel instead of a separate top-right cluster.
const vitalsPanel = createVitalsPanel({
  el: document.getElementById('vitals')!,
  store,
  nowMs: () => clock.nowMs(),
  focus,
  visibility,
  isVisibilityAvailable,
});
const { controlsSlot } = vitalsPanel;

// Control rail: settings group (Layers + Theme) and nav group (zoom + locate),
// folded into the vitals panel footer.
const rail = createControlRail(controlsSlot);
const tycoonButton=document.createElement('button');tycoonButton.className='control-btn';tycoonButton.textContent='Tycoon';tycoonButton.addEventListener('click',options.navigateTycoon);lifecycle.own(()=>tycoonButton.removeEventListener('click',options.navigateTycoon));rail.nav.append(tycoonButton);
const feedHealth = new FeedHealth(store);
lifecycle.own(() => feedHealth.destroy());
const layersPanel = createLayersPanel(rail.settings, visibility, feedHealth, isVisibilityAvailable);
lifecycle.own(() => layersPanel.destroy());
const aboutPanel = createAboutPanel(rail.settings, document.getElementById('about-content')!);
lifecycle.own(() => aboutPanel.destroy());
lifecycle.own(vitalsPanel.destroy);
createZoomControl(rail.nav, map);

// Browser geolocation: start live tracking on load (silently fails if denied),
// and a corner button to (re)acquire and fly the camera to the fix.
const geo = new GeoTracker();
geo.start();
lifecycle.own(() => geo.stop());
// On the first fix, if the user is in the city, frame the map on them once.
// Outside NYC we keep the default city view; the locate button still works anywhere.
if (!geolocationAutoCenterComplete) {
  const centerOnFirstFix = () => {
    const { fix } = geo.getState();
    if (!fix) return;
    geolocationAutoCenterComplete = true;
    offAutoCenter();
    // Recenter at the default zoom — don't zoom in further on load.
    if (isInNyc(fix.coords)) map.flyTo({ center: fix.coords });
  };
  const offAutoCenter = geo.onChange(centerOnFirstFix);
  lifecycle.own(offAutoCenter);
  centerOnFirstFix();
}
const locateButton = createLocateButton(rail.nav, { geo, map });
lifecycle.own(() => locateButton.destroy());

// RainViewer radar is a MapLibre raster overlay (not deck.gl): apply on each
// radar update, when the toggle flips, and once the map style is ready.
// Radar is suppressed when a layer is focused (solo mode hides everything else).
const applyRadarNow = () =>
  applyRadar(
    map,
    !focus.get() && visibility.isVisible('radar') ? store.get('radar') : undefined,
  );
lifecycle.own(store.onChange((layerId) => {
  if (layerId === 'radar') applyRadarNow();
}));
map.on('load', applyRadarNow);
lifecycle.own(visibility.onChange(applyRadarNow));
lifecycle.own(focus.onChange(applyRadarNow));

// TomTom traffic is opt-in and its source is absent while hidden, so MapLibre
// performs no provider requests until either traffic control is enabled.
const trafficController = createTrafficController({
  apiKey: TOMTOM_API_KEY,
  loadAttribution: fetchTomTomAttribution,
  apply: (options) => applyTraffic(map, options),
  onError: (error) => console.error('[traffic] attribution unavailable:', (error as Error).message),
});
const applyTrafficNow = (): void => trafficController.setEnabled(visibility.isVisible('traffic'));
map.on('load', applyTrafficNow);
lifecycle.own(visibility.onChange(applyTrafficNow));

const ambient = startDayNight({ el: document.getElementById('ambient')!, nowMs: () => clock.nowMs() });

// Theme switcher: restore the saved theme's tint policy, and on every swap
// re-apply the radar raster (setStyle wiped it) and retint the ambient overlay.
const initialThemeId = loadThemeId();
ambient.setEnabled(themeAllowsTint(initialThemeId));
const onThemeApplied = (themeId: string): void => {
  applyRadarNow();
  applyTrafficNow();
  ambient.setEnabled(themeAllowsTint(themeId));
};
const themeSwitcher = createThemeSwitcher(rail.settings, {
  map,
  currentThemeId: initialThemeId,
  onThemeApplied,
});
lifecycle.own(() => themeSwitcher.destroy());

// If the saved theme isn't the default dark, apply it once the initial base map
// has loaded (so we don't race the map's first style load).
if (initialThemeId !== 'dark') {
  map.once('load', () => applyTheme(map, initialThemeId, () => onThemeApplied(initialThemeId)));
}

// Static route maps (lines + stations) — geometry is fetched once per view and
// refetched only when the server announces a different geometry epoch (a new
// build: see epochedGeometry.ts), swapping in by plain assignment once the new
// payload has arrived. The deck.gl layers are rebuilt fresh each frame from
// that cached data. deck.gl Layer instances are single-use: a cached instance,
// once removed from the layer list (toggled off), is finalized and will not
// re-render if re-added, so toggling 'subwaymap' back on must hand deck a new instance.
const apiBase = import.meta.env.VITE_API_BASE ?? '';
let subwayGeometry: SubwayGeometry | null = null;
let subwayStationAxes: Record<string, SubwayTravelDirection[]> = {};
let lirrGeometry: MapGeometry | null = null;
let mnrGeometry: MapGeometry | null = null;
let pathGeometry: MapGeometry | null = null;
const geometryLoaders = [
  createEpochedGeometry<SubwayGeometry>({
    key: 'subway', url: subwayGeometryUrl(apiBase), epochs: options.geometryEpochs,
    onGeometry: (geometry) => {
      subwayStationAxes = subwayStationDirectionAxes(geometry);
      subwayGeometry = geometry;
    },
  }),
  createEpochedGeometry<MapGeometry>({
    key: 'lirr', url: lirrGeometryUrl(apiBase), epochs: options.geometryEpochs,
    onGeometry: (geometry) => { lirrGeometry = geometry; },
  }),
  createEpochedGeometry<MapGeometry>({
    key: 'mnr', url: mnrGeometryUrl(apiBase), epochs: options.geometryEpochs,
    onGeometry: (geometry) => { mnrGeometry = geometry; },
  }),
  createEpochedGeometry<MapGeometry>({
    key: 'path', url: pathGeometryUrl(apiBase), epochs: options.geometryEpochs,
    onGeometry: (geometry) => { pathGeometry = geometry; },
  }),
];
for (const loader of geometryLoaders) lifecycle.own(loader.destroy);
let stationExtents: SubwayStationExtentsPayload | null = null;
void fetchStationExtents(apiBase).then((payload) => {
  stationExtents = payload;
});
lifecycle.own(visibility.onChange(() => {
  if (!visibility.isVisible('subwaymap')) stationExtentSelection.clear();
}));

// ── Focus: solo mode + camera fit ────────────────────────────────────────────

/** Saved camera state before a focus was activated, for restoring on clear. */
let savedCamera: {
  center: maplibregl.LngLat | [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
} | null = options.initialState?.returnCamera ?? null;

/** The layerId that was successfully fitted — prevents re-fitting on each update. */
let fittedFocus: string | null = null;

/**
 * Compute camera padding that accounts for the vitals panel on desktop (left)
 * or the bottom strip on mobile.
 */
function focusPadding(): { top: number; right: number; bottom: number; left: number } {
  const rect = document.getElementById('vitals')!.getBoundingClientRect();
  // Desktop: panel is taller than wide (vertical column on the left)
  if (rect.height >= rect.width) {
    return { top: 60, right: 60, bottom: 60, left: Math.round(rect.right) + 30 };
  }
  // Mobile: panel is a bottom strip
  return { top: 60, right: 60, left: 60, bottom: Math.round(window.innerHeight - rect.top) + 30 };
}

lifecycle.own(focus.onChange(() => {
  const focusedId = focus.get();

  if (focusedId !== null) {
    // Save current camera position before we move (only once per focus session)
    if (savedCamera === null) {
      savedCamera = {
        center: map.getCenter(),
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      };
    }

    // Fit to the focused layer's features if data is already loaded, falling
    // back to static bounds for layers with no server-provided geometry (e.g.
    // the grid cables, which are a client-side constant).
    const features = store.get(focusedId)?.features ?? [];
    const bounds = featuresToBounds(features) ?? STATIC_FOCUS_BOUNDS[focusedId] ?? null;
    if (bounds !== null) {
      map.fitBounds(bounds, { padding: focusPadding(), duration: cameraDuration(), maxZoom: 15 });
      fittedFocus = focusedId;
    } else {
      // Data not yet loaded; late-data fit will handle it in store.onChange
      fittedFocus = null;
    }
  } else {
    // Focus cleared — fly back to where we were
    if (savedCamera !== null) {
      map.flyTo({
        center: savedCamera.center,
        zoom: savedCamera.zoom,
        bearing: savedCamera.bearing,
        pitch: savedCamera.pitch,
        duration: cameraDuration(),
      });
      savedCamera = null;
      fittedFocus = null;
    }
  }
}));

// Late-data fit: if a layer gets data after being focused, fit once.
// We also handle radar updates here (separate onChange above covers radar display).
lifecycle.own(store.onChange((layerId) => {
  if (layerId === 'radar') return;
  if (focus.get() === layerId && fittedFocus !== layerId) {
    const features = store.get(layerId)?.features ?? [];
    const bounds = featuresToBounds(features) ?? STATIC_FOCUS_BOUNDS[layerId] ?? null;
    if (bounds !== null) {
      map.fitBounds(bounds, { padding: focusPadding(), duration: cameraDuration(), maxZoom: 15 });
      fittedFocus = layerId;
    }
  }
}));

// Escape key clears focus
const onWindowKeyDown = (e: KeyboardEvent): void => {
  if (e.key === 'Escape') {
    focus.clear();
    stationExtentSelection.clear();
  }
};
window.addEventListener('keydown', onWindowKeyDown);
lifecycle.own(() => window.removeEventListener('keydown', onWindowKeyDown));

// Animation loop: rebuild deck.gl layers each frame via the RENDERERS registry.
// Advancing currentTime keeps animated layers (TripsLayer) gliding smoothly.
function frame() {
  // Latch whether this is the first frame back from a hidden tab BEFORE building any
  // layer, so every animator this frame snaps to its current position instead of
  // gliding through the movement that piled up while rAF was suspended.
  const nowSec = clock.nowSec();
  const focusedId = focus.get();
  const zoom = map.getZoom();

  const dynamic = RENDERERS
    .map(({ layerId, build }) => {
      const shown = isLayerSemanticallyVisible({
        layerId,
        zoom,
        focusedLayerId: focusedId,
        // Preserve the existing promise that explicit focus reveals its layer.
        userVisible: focusedId === layerId || visibility.isVisible(layerId),
      });
      if (!shown) return null;
      const state = store.get(layerId);
      return state ? build(state, nowSec) : null;
    })
    .filter((l) => l !== null);

  // Static map first so it draws underneath the live vehicles/markers. Suppressed
  // in solo mode — except when focusing the trains, since the subway route map is
  // the natural backdrop for live trains. Still respects the subway-map toggle.
  const showSubwayMap = focusedId === null || focusedId === 'subway';
  const statics =
    showSubwayMap && visibility.isVisible('subwaymap') && subwayGeometry
      ? (() => {
          const detail = stationExtents
            ? buildStationExtentLayers(stationExtents, zoom, stationExtentSelection.get())
            : [];
          return orderSubwayStaticLayers(
            detail,
            buildSubwayLinesLayer(subwayGeometry),
            buildSubwayStationsLayer(subwayGeometry),
          );
        })()
      : [];

  // Static LIRR map (branch lines + stations) drawn beneath the live LIRR trains,
  // mirroring the subway statics pattern. Visible when unfocused or focusing LIRR.
  const showLirrMap = focusedId === null || focusedId === 'lirr';
  const lirrStatics =
    showLirrMap && visibility.isVisible('lirrmap') && lirrGeometry
      ? [buildLirrLinesLayer(lirrGeometry), buildLirrStationsLayer(lirrGeometry)]
      : [];

  // Static Metro-North map beneath the live MNR trains, mirroring LIRR.
  const showMnrMap = focusedId === null || focusedId === 'mnr';
  const mnrStatics =
    showMnrMap && visibility.isVisible('mnrmap') && mnrGeometry
      ? [buildMnrLinesLayer(mnrGeometry), buildMnrStationsLayer(mnrGeometry)]
      : [];

  // Static PATH map beneath the live PATH trains, mirroring LIRR/MNR.
  const showPathMap = focusedId === null || focusedId === 'path';
  const pathStatics =
    showPathMap && visibility.isVisible('pathmap') && pathGeometry
      ? [buildPathLinesLayer(pathGeometry), buildPathStationsLayer(pathGeometry)]
      : [];

  // "You are here" pin on top of everything (client-side, not from the store).
  // Kept visible even during focus so the user keeps their bearings while a
  // single layer is solo'd; still respects the location toggle.
  const fix = visibility.isVisible('location') ? geo.getState().fix : null;
  const locationLayers = buildUserLocationLayers(fix, nowSec);
  overlay.setProps({ layers: [...statics, ...lirrStatics, ...mnrStatics, ...pathStatics, ...dynamic, ...locationLayers] });
}
return {frame,getState:()=>{const center=map.getCenter();const returnCenter=savedCamera?.center;return {center:[center.lng,center.lat],zoom:map.getZoom(),bearing:map.getBearing(),pitch:map.getPitch(),focus:focus.get(),returnCamera:savedCamera?{...savedCamera,center:Array.isArray(returnCenter)?returnCenter:[returnCenter!.lng,returnCenter!.lat]}:undefined};},destroy(){lifecycle.destroy();ambient.stop();stationHover.destroy();camPopup.hide();trafficController.setEnabled(false);map.remove();document.getElementById('map')!.style.display='none';document.querySelectorAll('.vital-controls,.vital-tip').forEach((node)=>node.remove());}};
}
